import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import styled, { css } from 'styled-components';

import { BLUE_500, BORDER_RADIUS_200, SPACE_050, Z_INDEX_4 } from 'styles/tokens';

const dropdownSurface = css`
  border-radius: ${BORDER_RADIUS_200};
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

export const MenuIconContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`;

/**
 * Trigger for the `custom` type, where the caller supplies the visuals but not an interactive
 * element. Radix needs a real button to attach keyboard activation to, so `customIcon` must render
 * none of its own — a clickable one nests a button inside a button.
 */
export const CustomTrigger = styled.button`
  all: unset;
  display: flex;
  width: 100%;

  &:focus-visible {
    outline: ${SPACE_050} solid ${BLUE_500};
    outline-offset: ${SPACE_050};
  }
`;

export const Container = styled.div<{ $noZIndex?: boolean; $showMenuButton: boolean }>`
  display: ${props => (props.$showMenuButton ? 'flex' : 'none')};
  position: relative;

  /* This needs to have a z-index of 4, because section filters use a z-index of 2, and page loaders use a z-index
   * of 3 which causes the user menu to appear hidden behind the filters
   */
  ${({ $noZIndex }) =>
    !$noZIndex &&
    css`
      z-index: ${Z_INDEX_4};
    `}
`;

/**
 * The open dropdown. Radix places it in both modes now, so this carries no offsets, no position
 * variants and no z-index of its own — inside a portal it stacks by mount order, which is what lets a
 * dialog opened from an item paint over it.
 */
export const MenuItemsContainer = styled(RadixDropdownMenu.Content)<{ $matchTriggerWidth: boolean }>`
  ${dropdownSurface}
  display: block;
  ${({ $matchTriggerWidth }) =>
    $matchTriggerWidth &&
    css`
      width: var(--radix-dropdown-menu-trigger-width);
    `}

  /* Radix marks a force-mounted closed content with data-state but does not hide it. */
  &[data-state='closed'] {
    display: none;
  }
`;

/**
 * Holds the items before the menu has ever been opened, so their count is known on first paint
 * without mounting a Radix content — which would hold a dismissable-layer entry, a positioning loop
 * and a focus scope whose guards sit in the tab order for the life of the page.
 */
export const ClosedMenuItemsContainer = styled.div`
  ${dropdownSurface}
  display: none;
`;
